import { Check, Minus, Plus, Info } from "lucide-react";
import { money, unitPrice, hasPricedOptions, effectiveOptions, slug, splitMulti } from "@/lib/api";
import { SampleLink } from "@/components/SampleLink";

export const OptionCard = ({
  product,
  selected,
  selectType,
  quantity,
  variants = {},
  optionSets = {},
  onToggle,
  onQty,
  onVariant,
  testId,
}) => {
  const price = selected ? unitPrice(product, variants, [], optionSets) : product.price;
  const groups = effectiveOptions(product, optionSets).variants;

  return (
    <div
      data-testid={testId}
      onClick={onToggle}
      className={`group relative cursor-pointer overflow-hidden rounded-lg border bg-card transition-all duration-200 hover:-translate-y-1 hover:shadow-lg ${
        selected ? "border-2 border-[#1B66C9] shadow-md" : "border-border"
      }`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-muted">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-secondary text-xs uppercase tracking-widest text-muted-foreground">
            No image
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        {selected && (
          <span className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full bg-[#1B66C9] text-white shadow-lg">
            <Check size={16} strokeWidth={3} />
          </span>
        )}
        {!selected && groups.length > 0 && (
          <span className="absolute bottom-3 left-3 rounded-full bg-black/55 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur">
            {groups.length} option{groups.length > 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-base font-bold leading-tight">{product.name}</h3>
          <span
            data-testid={`${testId}-price`}
            className="whitespace-nowrap font-display text-base font-black text-[#1B66C9]"
          >
            {!selected && hasPricedOptions(product, optionSets) && (
              <span className="mr-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">from</span>
            )}
            {money(price)}
            {product.unit && <span className="text-[10px] font-bold text-muted-foreground">/{product.unit}</span>}
            {Number(product.shipping) > 0 && (
              <span className="block whitespace-nowrap font-sans text-[10px] font-bold text-muted-foreground" data-testid={`${testId}-shipping`}>
                + {money(product.shipping)} shipping
              </span>
            )}
          </span>
        </div>
        {product.description && (
          <p className="mt-1.5 text-sm leading-snug text-muted-foreground">{product.description}</p>
        )}
        {product.note && (
          <p
            data-testid={`${testId}-note`}
            className="mt-2 flex items-start gap-1.5 rounded-md border border-[#1B66C9]/30 bg-[#1B66C9]/10 px-2.5 py-1.5 text-xs font-bold leading-snug text-[#144E9C]"
          >
            <Info size={13} className="mt-0.5 flex-shrink-0" /> {product.note}
          </p>
        )}
        {product.samples_enabled && (
          <div className="mt-2.5 flex">
            <SampleLink product={product} testId={testId} />
          </div>
        )}

        {selected && groups.length > 0 && (
          <div className="mt-3 space-y-2.5 border-t border-border pt-3" onClick={(e) => e.stopPropagation()}>
            {groups.map((g) => (
              <div key={g.name}>
                <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  {g.name}
                  {g.multi && <span className="ml-1 normal-case tracking-normal">· pick any</span>}
                </span>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {g.options.map((o) => {
                    const active = g.multi
                      ? o.label === "None"
                        ? splitMulti(variants[g.name]).length === 0
                        : splitMulti(variants[g.name]).includes(o.label)
                      : variants[g.name] === o.label;
                    return (
                      <button
                        key={o.label}
                        data-testid={`${testId}-variant-${slug(g.name)}-${slug(o.label)}`}
                        onClick={() => onVariant(g.name, o.label)}
                        className={`rounded-full border px-2.5 py-1 text-xs font-bold transition-colors ${
                          active
                            ? "border-[#1B66C9] bg-[#1B66C9] text-white"
                            : "border-border hover:border-foreground/40"
                        }`}
                      >
                        {o.label}
                        {Number(o.price_delta) > 0 && (
                          <span className={`ml-1 ${active ? "text-white/80" : "text-muted-foreground"}`}>
                            +{money(o.price_delta)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {selected && selectType === "multi" && (
          <div
            className="mt-3 flex items-center gap-3 border-t border-border pt-3"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{product.unit || "Qty"}</span>
            <div className="flex items-center gap-2">
              <button
                data-testid={`${testId}-qty-minus`}
                onClick={() => onQty(Math.max(1, quantity - 1))}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-border hover:bg-secondary"
              >
                <Minus size={14} />
              </button>
              {product.unit ? (
                <input
                  type="number"
                  min={1}
                  data-testid={`${testId}-qty-value`}
                  value={quantity}
                  onChange={(e) => onQty(Math.max(1, Number(e.target.value) || 1))}
                  className="h-7 w-16 rounded-md border border-border bg-background text-center text-sm font-bold"
                />
              ) : (
                <span className="w-6 text-center font-bold" data-testid={`${testId}-qty-value`}>
                  {quantity}
                </span>
              )}
              <button
                data-testid={`${testId}-qty-plus`}
                onClick={() => onQty(quantity + 1)}
                className="flex h-7 w-7 items-center justify-center rounded-md border border-border hover:bg-secondary"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
